import { useSite } from '../contexts/SiteContext'

/**
 * LanguageSwitcher - DE/EN language selector
 *
 * Simple text showing current language (DE or EN).
 * Click to toggle to the other language.
 * English enabled in Phase 6 with placeholder content.
 *
 * @param {string} className - Additional CSS classes
 * @param {boolean} compact - If true, shows as compact text next to burger
 * @param {boolean} onDark - If true (compact only), swap body→on-dark tokens
 *   so the switcher stays legible over dark sections. Driven by FloatingBurger's
 *   background probe.
 */
export default function LanguageSwitcher({ className = '', compact = false, onDark = false }) {
  const { currentLang, switchLanguage } = useSite()
  const isEnglishEnabled = true // English content enabled (Phase 6)

  const handleSwitch = () => {
    if (currentLang === 'de') {
      // Try to switch to English
      if (!isEnglishEnabled) {
        // Show brief warning and stay on German
        alert('English coming soon!')
        return
      }
      switchLanguage('en')
    } else {
      switchLanguage('de')
    }
  }

  if (compact) {
    // Compact: "DE" only, shows current language
    const compactColor = onDark
      ? 'text-primary-on-dark hover:text-secondary-on-dark'
      : 'text-color-primary hover:text-color-secondary'
    return (
      <span
        onClick={handleSwitch}
        className={`text-sm font-medium ${compactColor} cursor-pointer transition-colors ${className}`}
        title="Sprache wechseln / Switch language"
      >
        {currentLang.toUpperCase()}
      </span>
    )
  }

  // Full: "DE | EN" with both options
  const deClass = currentLang === 'de'
    ? 'text-color-primary font-medium'
    : 'text-color-text/60 hover:text-color-primary cursor-pointer'

  const enClass = currentLang === 'en'
    ? 'text-color-primary font-medium'
    : 'text-color-text/60 hover:text-color-primary cursor-pointer'

  return (
    <div className={`flex items-center gap-2 text-sm ${className}`}>
      <span
        onClick={() => currentLang !== 'de' && switchLanguage('de')}
        className={deClass + ' cursor-pointer transition-colors'}
      >
        DE
      </span>
      <span className="text-color-text/20">|</span>
      <span
        onClick={() => {
          if (!isEnglishEnabled) {
            alert('English coming soon!')
            return
          }
          if (currentLang !== 'en') switchLanguage('en')
        }}
        className={enClass + (isEnglishEnabled ? ' cursor-pointer transition-colors' : ' opacity-50 cursor-not-allowed')}
      >
        EN
      </span>
    </div>
  )
}
